import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Icon,
  Panel,
  SectionLabel,
  cx,
} from '@whinerp/ui';
import { formatCount, formatRelativeDate } from '@whinerp/contracts/format';
import type { QueuedWrite } from '@whinerp/client';
import { useFormat, useT } from '../app/i18n.js';
import { Overlay } from '../shell/Overlay.js';
import { useOfflineQueue } from './queue.js';

/**
 * What this device is still holding.
 *
 * The rule the whole surface is built around: **nothing here is ever described
 * as saved.** A count that says "3 waiting to send" is a fact the person can
 * act on - finish the call, find a signal, do not close the laptop. A green
 * tick over the same three writes is a lie they would only discover from a
 * colleague who could not see the note.
 *
 * It draws nothing at all when the queue is empty and the network is up.
 * A permanent status light trains people to stop reading it, and then the one
 * time it matters they do not notice it either.
 */

export function SyncStatus(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const [open, setOpen] = useState(false);
  const queue = useOfflineQueue();

  if (!queue.configured) return null;
  const quiet = queue.online && queue.writes.length === 0;
  if (quiet) return null;

  const count = queue.needsYou > 0 ? queue.needsYou : queue.waiting + queue.refused;
  const tone = queue.needsYou > 0 || queue.refused > 0 ? 'attention' : 'waiting';
  const summary =
    queue.needsYou > 0
      ? t('offline.indicator.needsYou', { values: { count: formatCount(queue.needsYou, format) } })
      : queue.writes.length > 0
        ? t('offline.indicator.waiting', { values: { count: formatCount(queue.writes.length, format) } })
        : t('offline.indicator.offline');

  return (
    <>
      <button
        type="button"
        data-component="SyncStatus"
        data-sync-state={queue.needsYou > 0 ? 'needs-you' : queue.online ? 'waiting' : 'offline'}
        data-sync-count={count}
        onClick={() => setOpen(true)}
        aria-label={t('offline.open')}
        className={cx(
          'inline-flex h-touch items-center gap-1.5 rounded-control px-2 text-meta',
          tone === 'attention'
            ? 'bg-warning-tint text-warning-text'
            : 'bg-sunken text-ink-600 hover:text-ink',
        )}
      >
        <Icon name={queue.online ? 'CloudArrowUp' : 'CloudSlash'} size="inline" decorative />
        {/* The words, not only the colour and the icon. This is the one
            control on the screen whose meaning somebody has to get right
            while distracted. */}
        <span>{summary}</span>
      </button>

      <Overlay open={open} onClose={() => setOpen(false)} label={t('offline.title')} className="w-panel-narrow">
        <QueuePanel onClose={() => setOpen(false)} />
      </Overlay>
    </>
  );
}

function QueuePanel({ onClose }: { onClose: () => void }): JSX.Element {
  const t = useT();
  const queue = useOfflineQueue();

  return (
    <div data-component="OfflineQueue" className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto p-4">
      {/* A div, not a header.
          <header> outside <main> is a banner landmark, and the shell already
          has one - the top bar. Two banners in a document leave a screen
          reader user with two "top of page" destinations and no way to tell
          which is which. Found by auditing the shell's overlays, which nothing
          had rendered before. */}
      <div className="flex items-center gap-3">
        <SectionLabel>{t('offline.title')}</SectionLabel>
        <span className="ms-auto flex items-center gap-2">
          <Button
            intent="secondary"
            data-sync-now
            disabled={queue.syncing || queue.writes.every((w) => w.state !== 'waiting')}
            onClick={() => void queue.replay()}
          >
            {queue.syncing ? t('offline.indicator.sending') : t('offline.sendNow')}
          </Button>
        </span>
      </div>

      {queue.writes.length === 0 ? (
        <EmptyState
          /* Nothing waiting to sync is the good outcome, not a gap to fill.
             An action here would be a button that does nothing useful. */
          reason="all-clear"
          illustration="CloudArrowUp"
          title={t('offline.empty.title')}
          body={t('offline.empty.body')}
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {queue.writes.map((write) => (
            <li key={write.id}>
              <QueuedRow write={write} onClose={onClose} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QueuedRow({ write, onClose }: { write: QueuedWrite; onClose: () => void }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queue = useOfflineQueue();
  const [confirming, setConfirming] = useState(false);

  const stateLabel =
    write.state === 'needs-you'
      ? t('offline.state.needsYou')
      : write.state === 'refused'
        ? t('offline.state.refused')
        : t('offline.state.waiting');

  return (
    <Panel data-queued-write={write.id} data-queued-state={write.state}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-body font-medium text-ink">{write.label}</span>
          <span
            data-queued-state-label
            className={cx(
              'rounded-chip px-1.5 py-0.5 text-meta',
              write.state === 'waiting' ? 'bg-sunken text-ink-600' : 'bg-warning-tint text-warning-text',
            )}
          >
            {stateLabel}
          </span>
          <span className="ms-auto text-meta text-ink-meta">
            {t('offline.queuedAt', {
              values: { when: formatRelativeDate(write.queuedAt, format) },
            })}
          </span>
        </div>

        {write.state === 'refused' && write.refusal ? (
          <p className="text-dense text-ink-600">
            {t('offline.refusedBecause', { values: { reason: write.refusal } })}
          </p>
        ) : null}

        {write.state === 'needs-you' ? <ConflictChoice write={write} onSettled={onClose} /> : null}

        <div className="flex items-center gap-2">
          <Button intent="ghost" data-discard-write onClick={() => setConfirming(true)}>
            {t('offline.discard')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t('offline.discard')}
        confirmLabel={t('offline.discard')}
        confirmIntent="danger"
        labels={{ cancel: t('action.cancel') }}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          void queue.discard(write.id);
        }}
      >
        {t('offline.discard.confirm')}
      </ConfirmDialog>
    </Panel>
  );
}

/**
 * The decision, field by field, with both values shown.
 *
 * Never a single "resolve" button. The person is not choosing between two
 * records, they are choosing between two values of one field, and the two are
 * shown side by side because a choice made from memory of what you typed is
 * not a choice.
 */
function ConflictChoice({ write, onSettled }: { write: QueuedWrite; onSettled: () => void }): JSX.Element {
  const t = useT();
  const queue = useOfflineQueue();
  const conflicts = ((write.response as { conflicts?: Conflict[] } | null)?.conflicts ?? []) as Conflict[];

  return (
    <div className="flex flex-col gap-2" data-conflicts>
      <p className="text-dense text-ink-600">{t('offline.conflict.body')}</p>
      {conflicts.map((conflict) => (
        <div
          key={conflict.field}
          data-conflict-field={conflict.field}
          className="flex flex-col gap-1 rounded-control border border-rule p-2"
        >
          <p className="text-meta text-ink-label">
            {t('offline.conflict.title', { values: { field: conflict.field } })}
          </p>
          <p className="text-meta text-ink-meta">{conflict.by}</p>
          <div className="flex flex-wrap gap-2">
            <Choice
              kind="yours"
              label={t('offline.conflict.yours')}
              value={String(conflict.yours ?? '')}
              action={t('offline.conflict.keepYours')}
              onChoose={() => void settle(conflict, 'yours')}
            />
            <Choice
              kind="theirs"
              label={t('offline.conflict.theirs')}
              value={String(conflict.theirs ?? '')}
              action={t('offline.conflict.keepTheirs')}
              onChoose={() => void settle(conflict, 'theirs')}
            />
          </div>
        </div>
      ))}
    </div>
  );

  async function settle(conflict: Conflict, keep: 'yours' | 'theirs'): Promise<void> {
    // No store, no queue, no conflict to settle. Unreachable from the panel,
    // which only draws when there is one - stated rather than asserted away.
    if (!queue.store) return;
    const { resolveQueued } = await import('@whinerp/client');
    /*
     * Keeping theirs is still a write, and it is sent.
     *
     * The tempting shortcut is to drop the queued item and call it resolved -
     * the stored value is already theirs, so nothing needs to change. It is
     * wrong for the fields that were not in conflict: the queued write also
     * carried edits nobody disputed, and dropping it would throw those away
     * along with the decision.
     */
    const body = write.spec.body as { changes: Record<string, { from: unknown; to: unknown }> };
    const changes: Record<string, { from: unknown; to: unknown }> = {};
    for (const [field, change] of Object.entries(body.changes)) {
      const disputed = conflicts.find((c) => c.field === field);
      if (!disputed) {
        changes[field] = change;
        continue;
      }
      const decided = field === conflict.field ? keep : 'theirs';
      // `from` becomes what is actually stored, so the server's check passes
      // on the second attempt for the right reason: the client now knows what
      // it is overwriting rather than asserting a premise it has been told is
      // false.
      changes[field] = { from: disputed.theirs, to: decided === 'yours' ? disputed.yours : disputed.theirs };
    }

    await resolveQueued(queue.store, write, { ...write.spec, body: { changes } });
    await queue.refresh();
    onSettled();
  }
}

interface Conflict {
  field: string;
  yours: unknown;
  theirs: unknown;
  by: string;
}

function Choice({
  kind,
  label,
  value,
  action,
  onChoose,
}: {
  kind: 'yours' | 'theirs';
  label: string;
  value: string;
  action: string;
  onChoose: () => void;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 rounded-control bg-sunken p-2">
      <p className="text-meta text-ink-label">{label}</p>
      <p data-conflict-value={kind} className="truncate text-body text-ink">
        {value}
      </p>
      <Button intent="secondary" data-keep={kind} onClick={onChoose}>
        {action}
      </Button>
    </div>
  );
}
